import React from "react";
import { Navbar } from "../components/app/Navbar";
import { Footer } from "../components/app/Footer";
import { useT } from "../context/LanguageContext";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="font-display text-xl font-semibold text-gray-900">{title}</h2>
    <div className="mt-3 space-y-3 text-gray-700 leading-relaxed text-[15px]">{children}</div>
  </section>
);

export default function Privacy() {
  const { t, lang } = useT();
  const locale = lang === "es" ? "es-ES" : "en-US";
  const lastUpdated = new Date().toLocaleDateString(locale, {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="min-h-screen flex flex-col bg-[#f9f9f8]" data-testid="privacy-page">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-green-800">{t("legal.eyebrow")}</p>
        <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold text-gray-900" data-testid="privacy-title">
          {t("privacy.title")}
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          {t("legal.lastUpdated")} {lastUpdated}
        </p>

        <p className="mt-6 text-gray-600 leading-relaxed text-[15px]">
          {t("privacy.intro")}
        </p>

        <div className="mt-10 space-y-10">
          <Section title={t("privacy.collectTitle")}>
            <p data-testid="privacy-data-collected">{t("privacy.collectIntro")}</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>{t("privacy.collect1")}</li>
              <li>{t("privacy.collect2")}</li>
              <li>{t("privacy.collect3")}</li>
              <li>{t("privacy.collect4")}</li>
              <li>{t("privacy.collect5")}</li>
            </ul>
            <p>{t("privacy.cardNote")}</p>
          </Section>

          <Section title={t("privacy.useTitle")}>
            <p data-testid="privacy-data-usage">{t("privacy.useBody1")}</p>
            <p>{t("privacy.useBody2")}</p>
          </Section>

          <Section id="sms" title={t("privacy.smsNotificationsTitle")}>
            <p data-testid="privacy-sms-notifications">{t("privacy.smsNotificationsBody")}</p>
            <p data-testid="privacy-sms-consent-data-usage">{t("privacy.smsConsentDataUsage")}</p>
            {/* Single unified no-sharing statement — appears EXACTLY ONCE
                in the entire Privacy Policy. Consolidates the prior
                duplicate "mobile not shared", "consent data never sold",
                "consent data alt-wording", and "consent not shared"
                paragraphs into one canonical sentence Twilio A2P 10DLC
                reviewers can lock onto. */}
            <p data-testid="privacy-sms-unified-not-shared">{t("privacy.smsUnifiedNotShared")}</p>

            {/* SMS Program URLs — Twilio A2P 10DLC reviewers expect to
                see a clear cross-reference between the three public
                documents (program details, privacy, terms). Rendering
                them as a labelled list inside the SMS section gives them
                a single anchor to crawl. */}
            <h3
              className="font-display text-base sm:text-lg font-semibold text-gray-900 tracking-tight pt-2"
              data-testid="privacy-sms-program-urls-title"
            >
              {t("privacy.smsProgramUrlsTitle")}
            </h3>
            <ul className="space-y-2 text-gray-600">
              <li data-testid="privacy-sms-program-url-details">
                <span className="font-medium text-gray-900">{t("privacy.smsProgramDetailsLabel")}</span>{" "}
                <a
                  href="https://cleaning.tasklio.co/sms-opt-in"
                  className="text-green-800 hover:underline break-all"
                >
                  https://cleaning.tasklio.co/sms-opt-in
                </a>
              </li>
              <li data-testid="privacy-sms-program-url-privacy">
                <span className="font-medium text-gray-900">{t("privacy.smsPolicyLinkLabel")}</span>{" "}
                <a
                  href="https://cleaning.tasklio.co/privacy"
                  className="text-green-800 hover:underline break-all"
                >
                  https://cleaning.tasklio.co/privacy
                </a>
              </li>
              <li data-testid="privacy-sms-program-url-terms">
                <span className="font-medium text-gray-900">{t("privacy.smsTermsLinkLabel")}</span>{" "}
                <a
                  href="https://cleaning.tasklio.co/terms"
                  className="text-green-800 hover:underline break-all"
                >
                  https://cleaning.tasklio.co/terms
                </a>
              </li>
            </ul>
          </Section>

          {/* SMS Consent — Twilio A2P 10DLC compliance. Documents the exact
              opt-in event, lawful use of the data, frequency, rates, and
              the STOP/HELP commands. Placed directly under the SMS
              Communications section so the consent record reads top-to-
              bottom in one cohesive block for campaign reviewers. */}
          <Section id="sms-consent" title={t("privacy.smsConsentSectionTitle")}>
            <p data-testid="privacy-sms-consent-intro">{t("privacy.smsConsentIntro")}</p>
            <p data-testid="privacy-sms-consent-saving">{t("privacy.smsConsentSavingIs")}</p>
            <p data-testid="privacy-sms-consent-usage">{t("privacy.smsConsentUsage")}</p>
            <ul className="list-disc pl-6 space-y-1 text-gray-600">
              <li data-testid="privacy-sms-consent-frequency">{t("privacy.smsConsentFrequency")}</li>
              <li data-testid="privacy-sms-consent-rates">{t("privacy.smsConsentRates")}</li>
              <li data-testid="privacy-sms-consent-stop">{t("privacy.smsConsentStop")}</li>
              <li data-testid="privacy-sms-consent-help">{t("privacy.smsConsentHelp")}</li>
            </ul>
          </Section>

          <Section title={t("privacy.dontTitle")}>
            <p data-testid="privacy-no-selling">{t("privacy.dontBody1")}</p>
            <p>{t("privacy.dontBody2")}</p>
          </Section>

          <Section title={t("privacy.choicesTitle")}>
            <p>
              {t("privacy.choicesBody1Pre")}{" "}
              <span className="font-medium text-gray-900">{t("privacy.settingsWord")}</span>
              {t("privacy.choicesBody1Post")}
            </p>
            <p>{t("privacy.choicesBody2")}</p>
          </Section>

          <Section title={t("privacy.contactTitle")}>
            <p>
              {t("privacy.contactBody")}{" "}
              <a
                href="mailto:support@tasklio.co"
                className="text-green-800 hover:underline font-medium"
                data-testid="privacy-contact-link"
              >
                support@tasklio.co
              </a>
              .
            </p>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
